import {
  Button,
  Card,
  DataList,
  PageHeader,
  StatusBadge,
  type DataColumn,
} from "@nahal/ui";
import type { Domain } from "@nahal/api-client";
import { useChangeDomainStatus, useOpsDomains } from "../../lib/queries";

/**
 * Domains waiting on Nahal Labs. Verification, DNS, ingress and the certificate are all manual today,
 * so this is a worklist rather than a monitor -- and the status a staff member sets here is exactly
 * what the customer sees on their own Domains screen, which is the point of it existing at all.
 *
 * Live domains are deliberately absent: once one is serving there is nothing left to do to it, and a
 * queue that keeps finished work is a queue people stop reading.
 */
export function DomainQueueScreen() {
  const domains = useOpsDomains();
  const change = useChangeDomainStatus();

  const columns: Array<DataColumn<Domain>> = [
    { key: "hostname", header: "Domain", primary: true, cell: (row) => row.hostname },
    { key: "customer", header: "Customer", cell: (row) => row.organizationName },
    {
      key: "workspace",
      header: "Workspace",
      desktopOnly: true,
      cell: (row) => `${row.productCode} · ${row.workspaceCode}`,
    },
    { key: "kind", header: "Use", desktopOnly: true, cell: (row) => label(row.kind) },
    {
      key: "token",
      header: "TXT record",
      desktopOnly: true,
      cell: (row) => (
        <span className="break-all text-xs text-[var(--text-secondary)]">
          {row.verificationToken ?? "-"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => <StatusBadge status={label(row.status)} tone={tone(row.status)} />,
    },
    {
      key: "asked",
      header: "Asked",
      desktopOnly: true,
      cell: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <>
      <PageHeader
        title="Domains"
        description="Customer domains waiting on DNS verification, ingress or a certificate."
      />

      <Card>
        <DataList
          columns={columns}
          rows={domains.data ?? []}
          rowKey={(row) => row.id}
          loading={domains.isLoading}
          error={domains.isError ? "Could not load the domain queue." : null}
          empty={{ title: "Nothing waiting", description: "Every customer domain is live." }}
          rowActions={(row) => (
            <>
              {row.status === "PENDING_VERIFICATION" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => change.mutate({ domainId: row.id, status: "VERIFIED" })}
                >
                  Mark verified
                </Button>
              )}
              {row.status === "VERIFIED" && (
                <Button
                  size="sm"
                  variant="solid"
                  onClick={() => change.mutate({ domainId: row.id, status: "LIVE" })}
                >
                  Mark live
                </Button>
              )}
              {row.status !== "FAILED" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => change.mutate({ domainId: row.id, status: "FAILED" })}
                >
                  Mark failed
                </Button>
              )}
              {row.status === "FAILED" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => change.mutate({ domainId: row.id, status: "PENDING_VERIFICATION" })}
                >
                  Reopen
                </Button>
              )}
            </>
          )}
        />
      </Card>
    </>
  );
}

const label = (value: string) =>
  value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, " ");

const tone = (status: Domain["status"]) =>
  status === "LIVE" ? "success" : status === "FAILED" ? "danger" : "warning";
